/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import VeezuBBCLogo from 'assets/img/Veezu-BBC/Veezu-BBC-BlackCoral.svg'
import * as React from 'react'
import './veezu-login-style.scss'

import { useMsal } from '@azure/msal-react'
import { Typography } from '@mui/material'
import { userScopeAPI } from 'authConfig'
import { clearTransientAuthStorage, getB2CAuthority } from 'helpers/authFlow'
import useSSOEmail from 'hooks/useSSOEmail'
import { useDispatch, useSelector } from 'react-redux'
import { setB2CPolicy } from 'reducers/authReducer'

const VeezuBBCLoginPage = () => {
  const { instance } = useMsal()
  const dispatch = useDispatch()
  const [emailValue, setEmailValue] = React.useState<string>('')
  const { b2cPolicy, email } = useSelector((state: any) => state.auth)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)

  const verifyEmail = (value: string) => {
    const emailRex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    if (emailRex.test(value)) {
      return true
    }
    return false
  }

  async function handleSSORedirect() {
    if (window.location.hash) {
      clearTransientAuthStorage()
      localStorage.setItem('loginPolicy', 'B2C_1A_SIGNUP_SIGNIN')
      await dispatch(setB2CPolicy({ b2cPolicy: 'B2C_1A_SIGNUP_SIGNIN' }))

      const accounts = instance.getAllAccounts()
      if (accounts.length === 0) {
        await instance.loginRedirect({
          authority: getB2CAuthority('B2C_1A_SIGNUP_SIGNIN'),
          scopes: [userScopeAPI],
        })
      }
    }
  }

  const handleEmailValidation = (value: string) => {
    const emailInputWrapper = document.getElementById('email-input--field')
    const emailInputErrorMsg = document.getElementById('email-input--error-msg')

    const isValidEmail = verifyEmail(value)

    if (isValidEmail === false) {
      emailInputWrapper?.classList.add('email-input--error')
      emailInputErrorMsg?.classList.add('show')
    } else {
      emailInputWrapper?.classList.remove('email-input--error')
      emailInputErrorMsg?.classList.remove('show')
    }
  }

  const handleSSOCheck = (value: string) => {
    const emailInputWrapper = document.getElementById('email-input--field')
    const emailInputErrorMsg = document.getElementById('email-SSO--error-msg')

    const isValidSSO = useSSOEmail(emailValue)
    const isValidEmail = verifyEmail(emailValue)

    if (isValidEmail && !isValidSSO && value === 'B2C_1A_SIGNUP_SIGNIN') {
      emailInputWrapper?.classList.add('email-SSO--error')
      emailInputErrorMsg?.classList.add('show')
    } else {
      emailInputWrapper?.classList.remove('email-SSO--error')
      emailInputErrorMsg?.classList.remove('show')
    }
  }

  const checkInput = () => {
    const emailInput = document.getElementById('email') as any
    const lblEmail = document.querySelector('.lbl-email')
    const emailInputValue = emailInput?.value || email

    if (emailInputValue && lblEmail) {
      setEmailValue(emailInputValue || '')
      lblEmail.classList.add('lbl-email--up')
    } else {
      if (lblEmail) {
        lblEmail.classList.remove('lbl-email--up')
      }
    }
  }

  const handleLoginRedirect = (loginPolicy: string) => {
    clearTransientAuthStorage()

    handleEmailValidation(emailValue)
    handleSSOCheck(loginPolicy)
    const isValidEmail = verifyEmail(emailValue)

    if (isValidEmail) {
      if (loginPolicy === 'B2C_1A_SIGNUP_SIGNIN') {
        if (useSSOEmail(emailValue)) {
          setIsLoading(true)
          localStorage.setItem('loginPolicy', loginPolicy)
          localStorage.setItem('emailHint', emailValue ? emailValue : '')
          dispatch(setB2CPolicy({ b2cPolicy: loginPolicy, email: emailValue }))
        }
      }
      if (loginPolicy === 'B2C_1A_SIGNIN_LOCAL') {
        setIsLoading(true)
        localStorage.setItem('loginPolicy', loginPolicy)
        localStorage.setItem('emailHint', emailValue ? emailValue : '')
        dispatch(setB2CPolicy({ b2cPolicy: loginPolicy, email: emailValue }))
      }
    }
  }

  const setupActiveAccount = () => {
    const accounts = instance.getAllAccounts()
    if (accounts.length > 0) {
      instance.setActiveAccount(accounts[0])
    }
  }

  React.useEffect(() => {
    setupActiveAccount()

    const hasMsalInteraction = document.cookie.includes(
      'msal.interaction.status',
    )
    if (hasMsalInteraction) {
      setIsLoading(true)

      const timeoutId = setTimeout(() => {
        console.warn(
          '[LoginPage] MSAL interaction timed out, clearing stale interaction cookie',
        )
        document.cookie =
          'msal.interaction.status=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
        setIsLoading(false)
      }, 10000)

      return () => clearTimeout(timeoutId)
    }
  }, [])

  React.useEffect(() => {
    const storedEmailHint = localStorage.getItem('emailHint')

    if (b2cPolicy && storedEmailHint && storedEmailHint !== '') {
      checkInput()

      const emailHint = storedEmailHint
      const emailDomain = storedEmailHint?.split('@')[1]

      if (!emailHint) {
        console.error('[LoginPage] No emailHint available for login redirect')
        return
      }

      console.log('[LoginPage] Initiating loginRedirect with:', {
        policy: b2cPolicy,
        emailHint,
        emailDomain,
      })

      instance
        .loginRedirect({
          authority: getB2CAuthority(b2cPolicy),
          scopes: [userScopeAPI],
          domainHint: emailDomain,
          loginHint: emailHint,
          extraQueryParameters: {
            logonLocation: 'veezu',
            mainAppLink: window.location.origin,
          },
        })
        .catch((error) => {
          console.error('[LoginPage] MSAL loginRedirect failed:', error)
          setIsLoading(false)
        })
    } else if (!storedEmailHint || storedEmailHint === '') {
      handleSSORedirect()
    }
  }, [b2cPolicy])

  return (
    <div className="veezu-login-wrapper">
      <div className="container">
        <div className="side">
          <div className="backdrop" />
          <div className="brand">
            <img
              alt="Veezu BBC Logo"
              src={VeezuBBCLogo}
              height={200}
              width={400}
            />
          </div>
        </div>
        <div className="main">
          <div className="login-form" id="app">
            <div className="login-label">
              <Typography variant="h4">Welcome to Veezu BBC</Typography>
              <Typography
                variant="body1"
                style={{ color: 'gray', paddingTop: '20px' }}
              >
                Please enter your email to continue
              </Typography>
            </div>
            {!isLoading && (
              <div className="login-form-container">
                <div className="email-input">
                  <div className="email-input--field" id="email-input--field">
                    <label htmlFor="email" className="lbl-email">
                      Email
                    </label>
                    <input
                      id="email"
                      defaultValue={emailValue}
                      type="email"
                      onInput={checkInput}
                    />
                  </div>
                  <div
                    className="email-input--error-msg"
                    id="email-input--error-msg"
                  >
                    Please provide a valid email
                  </div>
                  <div
                    className="email-input--error-msg"
                    id="email-SSO--error-msg"
                  >
                    Please contact your IT Support for SSO Logon
                  </div>
                </div>
                <button
                  className="login-btn"
                  onClick={() => handleLoginRedirect('B2C_1A_SIGNIN_LOCAL')}
                >
                  Log In
                </button>
                <div className="footer"></div>
              </div>
            )}
            {isLoading && <div className="loader">Loading...</div>}
          </div>
        </div>
      </div>
    </div>
  )
}

export default VeezuBBCLoginPage
